import {
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import {
  MatExpButton,
  MatExpIconButton,
  MatExpLoadingIndicator,
} from '@camaradesuk/m3e-angular';
import { Store } from '@ngrx/store';
import { interval, map, startWith } from 'rxjs';
import { selectNotificationEmail } from '@app/generated/feature-flags.selectors';
import type { NotificationDto } from '@core/services/api-client.generated';
import {
  NotificationCardComponent,
  NotificationReadChange,
  NotificationReplyRequest,
} from '../notification-card/notification-card.component';
import { NotificationInboxService } from '../notification-inbox.service';
import { NotificationNoticeActions } from '../notification-notice-actions.service';
import { isAnswered, isAvailable } from '../notification-presentation';

/**
 * The bell panel (N4): the newest notices over the current page, in a compact Material 3 menu-like surface modelled
 * on the Google Cloud console's panel (prototype `Main.dc.html`, the `pop` popover). A centred title with no count,
 * an options menu (Mark all as read, Clear all, Email preferences), rows that expand in place with N3's card, and
 * "See all in Notifications". Opening a row marks it read (D49). Housekeeping goes through the same actions as the
 * page, so it behaves identically. The bell owns opening, closing and focus return; the panel owns its list.
 *
 * Focus moves into the panel when it opens, but it is not trapped there (a non-modal popover): after Dismiss or Clear
 * all, Tab must be able to reach the snackbar's Undo, which sits outside the panel (review of #4651). Escape, the bell
 * and a click outside close it.
 */
@Component({
  selector: 'syrf-notification-panel',
  templateUrl: './notification-panel.component.html',
  styleUrl: './notification-panel.component.scss',
  imports: [
    RouterLink,
    MatButton,
    MatIconButton,
    MatIcon,
    MatMenuModule,
    MatExpButton,
    MatExpIconButton,
    MatExpLoadingIndicator,
    NotificationCardComponent,
  ],
  // Angular host binding keys use template syntax, not identifiers.
  /* eslint-disable @typescript-eslint/naming-convention */
  host: {
    class: 'syrf-notification-panel syrf-expressive-buttons',
    '[class.is-compact]': 'compact()',
  },
  /* eslint-enable @typescript-eslint/naming-convention */
})
export class NotificationPanelComponent {
  readonly inbox = inject(NotificationInboxService);
  private readonly _notice = inject(NotificationNoticeActions);
  private readonly _injector = inject(Injector);
  /** Phone width (below 600 px): a full-width sheet, the cards in their phone layout (P1). */
  readonly compact = input(false);
  /** The Email preferences menu item shows only while `notificationEmail` is on (its route is gated the same way). */
  readonly emailEnabled = inject(Store).selectSignal(selectNotificationEmail);
  /** The phone sheet's Close button (there is no Escape key on a phone); the bell closes the panel. */
  readonly closeRequested = output<void>();

  private readonly _surface =
    viewChild.required<ElementRef<HTMLElement>>('surface');
  private readonly _list = viewChild.required<ElementRef<HTMLElement>>('list');
  private readonly _cards = viewChildren(NotificationCardComponent);

  /** A ticking clock for relative times. */
  protected readonly now = toSignal(
    interval(60000).pipe(
      startWith(0),
      map(() => new Date())
    ),
    { initialValue: new Date() }
  );

  readonly expandedId = signal<string | null>(null);

  /**
   * The rows: the newest of All. The server never lists there a dismissed notice, one the reader can no longer open
   * (N3 criterion 14), or a finished one (resolved, or an answered invitation: they live under Resolved, N4; a release
   * hidden on What's New is not finished, N7); a row
   * that still arrives so (a write that has not been re-read yet) is dropped, so none of them shows in the panel.
   */
  readonly rows = computed(
    () =>
      this.inbox.panelItems()?.filter(
        (item) =>
          !item.dismissedAtUtc &&
          // A notice just answered with a quick reply stays where it is until the panel closes (L7, Q5).
          (!item.resolvedAtUtc || this.inbox.isHeld(item.id)) &&
          isAvailable(item) &&
          !isAnswered(item)
      ) ?? null
  );

  /**
   * Mark all as read acts on every unread notice, not only the rows shown (it reads the Unread list), so it follows the
   * unread count rather than the rows: enabled while the badge shows a number (capped counts included), disabled at
   * zero even if a shown row is unread but no longer counts (an answered workflow never counts).
   */
  readonly canMarkAllRead = computed(
    () => this.inbox.unreadCount() > 0 || this.inbox.unreadCountCapped()
  );
  /**
   * Clear all (option B) clears every notice of All, not only the rows shown, except what waits for the reader's
   * answer: offered while the panel shows any row (the server decides what can be cleared).
   */
  readonly canClearAll = computed(() => !!this.rows()?.length);

  /** "See all" opens the page; with a row open, the page opens with that notice expanded. */
  readonly seeAllLink = computed(() => {
    const id = this.expandedId();
    return id ? ['/notifications', id] : ['/notifications'];
  });

  constructor() {
    void this.inbox.openPanel();
    inject(DestroyRef).onDestroy(() => this.inbox.closePanel());
    // Focus moves into the panel (its surface, named "Notifications"); Tab then reaches the options button and rows.
    afterNextRender(() => this._surface().nativeElement.focus());
  }

  protected isBusy(id: string): boolean {
    return this._notice.isBusy(id);
  }

  /** Opening a row marks it read for the reader (D49); closing changes nothing. One row is open at a time. */
  async toggle(item: NotificationDto): Promise<void> {
    if (this.expandedId() === item.id) {
      this.expandedId.set(null);
      return;
    }
    this.expandedId.set(item.id);
    this._reveal(item.id);
    this._notice.noteOpened(item);
    if (!item.readAtUtc) await this._notice.markRead(item.id);
  }

  setRead(
    item: NotificationDto,
    change: NotificationReadChange
  ): Promise<void> {
    return this._notice.setRead(item, change);
  }

  async dismiss(item: NotificationDto): Promise<void> {
    const index = (this.rows() ?? []).findIndex((row) => row.id === item.id);
    if (this.expandedId() === item.id) this.expandedId.set(null);
    if (!(await this._notice.dismiss(item.id))) return;
    this._focusAfterRemoval(index);
  }

  /**
   * Accept or Decline inline. An answered invitation leaves the panel (it lives under Resolved, N4): the shared actions
   * remove its row as soon as the server confirms (not when a re-read lands), and the panel closes it and moves focus
   * as a dismiss does (the next row, else the previous one, else the panel); the snackbar says what happened and where
   * the notice went. A refusal keeps the row, and focus, unless the server no longer lists it.
   */
  async answer(
    item: NotificationDto,
    answer: 'accept' | 'decline'
  ): Promise<void> {
    const index = (this.rows() ?? []).findIndex((row) => row.id === item.id);
    const outcome = await this._notice.answer(item, answer);
    if (outcome === 'cancelled') return;
    if (outcome === 'refused' && this.rows()?.some((row) => row.id === item.id))
      return;
    if (this.expandedId() === item.id) this.expandedId.set(null);
    this._focusAfterRemoval(index);
  }

  /**
   * The quick reply (L7): the shared actions send it; once confirmed the row stays where it is, marked "You replied",
   * until the panel closes, and then lives under Resolved (owner decision Q5).
   */
  reply(
    item: NotificationDto,
    request: NotificationReplyRequest
  ): Promise<boolean> {
    return this._notice.reply(item, request);
  }

  /** Mark all as read means every unread notice, not only the few rows shown here. */
  async markAllRead(): Promise<void> {
    let ids: string[];
    try {
      ids = await this.inbox.unreadIds();
    } catch {
      this._notice.say(
        'The notices could not be marked as read. Please try again.'
      );
      return;
    }
    await this._notice.markAllRead(ids);
  }

  /**
   * Clear all, option B: every notice of All (the panel's set, no filters), loaded or not, as of the panel's read time,
   * except what waits for the reader's answer; one snackbar and one Undo.
   */
  async clearAll(): Promise<void> {
    if (!this.canClearAll()) return;
    this.expandedId.set(null);
    const cleared = await this._notice.clearAll({
      status: 'all',
      asOfUtc: this.inbox.panelAsOfUtc(),
    });
    if (cleared.length) this._focusAfterRemoval(0);
  }

  /**
   * Once an opened row has grown, scroll the list just enough to show all of it (prototype `scrollOpenCard`), keeping
   * its top in view when it is taller than the list, so the message and its buttons are not left below the fold.
   */
  private _reveal(id: string): void {
    afterNextRender(
      () => {
        const list = this._list().nativeElement;
        const card = list.querySelector<HTMLElement>(
          `[data-nid="${CSS.escape(id)}"]`
        );
        if (!card) return;
        // The list is the cards' offset parent (position: relative), so this ignores its scroll.
        const top = card.offsetTop;
        const bottom = top + card.offsetHeight;
        const scroll = Math.min(
          top,
          Math.max(list.scrollTop, bottom - list.clientHeight)
        );
        if (scroll !== list.scrollTop) list.scrollTop = scroll;
      },
      { injector: this._injector }
    );
  }

  /** After a row leaves, focus the next one (the previous when it was last, the panel when none is left). */
  private _focusAfterRemoval(index: number): void {
    afterNextRender(
      () => {
        const cards = this._cards();
        const target = cards[Math.min(Math.max(index, 0), cards.length - 1)];
        if (target) target.focus();
        else this._surface().nativeElement.focus();
      },
      { injector: this._injector }
    );
  }
}
